import { StorageDb } from "./StorageDb";

export type TileData = {
    image: Blob;
};

export type TileMetadata = {
    refCount: number;
    timestamp: number;
};

export enum TilePutIncRef {
    No,
    Yes,
}

export enum TileRemoveDecRef {
    No,
    Yes,
}

export class TileCache {
    private readonly _cache = new StorageDb<TileMetadata>("tile-cache", Number.MAX_VALUE);
    private readonly _dataCache = new StorageDb<TileData>("tile-data-cache", Number.MAX_VALUE);

    public async init() {}

    // Remove the leading subdomain as this maybe be set to one of several names
    // to avoid overloading a single tile server. These are randomly chosen so
    // breaks caching if the initial request comes from a different subdomain to a
    // subsequent one.
    private _cleanUrl(url: string) {
        const u = new URL(url);
        u.hostname = u.hostname.split(".").slice(1).join(".");
        return u.toString();
    }

    /**
     * Remove a tile from a cache. If the decRef parameter is Yes then the reference count
     * will be decremented and if it is 0 the tile will be deleted.
     */
    public async removeTile(srcUrl: string, decRef: TileRemoveDecRef = TileRemoveDecRef.No) {
        const url = this._cleanUrl(srcUrl);
        await this._cache.ready;
        const existing = await this.getTileMetadata(url);
        if (existing) {
            if (decRef === TileRemoveDecRef.Yes) {
                existing.refCount -= 1;
            }

            if (existing.refCount === 0) {
                await this._cache.delete(url);
                await this._dataCache.delete(url);
            }
        }
    }

    public async putTile(
        srcUrl: string,
        timestamp: number,
        image: Blob,
        incRef: TilePutIncRef = TilePutIncRef.No
    ) {
        const url = this._cleanUrl(srcUrl);
        let metadata = await this.getTileMetadata(url);
        if (metadata) {
            if (incRef === TilePutIncRef.Yes) {
                metadata.refCount += 1;
            }
        } else {
            metadata = {
                refCount: 0,
                timestamp,
            };
            await this._dataCache.set(url, {
                image,
            });
        }
        await this._cache.set(url, metadata);
    }

    public async getTileMetadata(srcUrl: string): Promise<TileMetadata | undefined> {
        const url = this._cleanUrl(srcUrl);
        try {
            const entry = await this._cache.get(url);
            if (entry) {
                return entry;
            }
        } catch (_error) {
            return undefined;
        }

        return undefined;
    }

    public async getTileImage(srcUrl: string): Promise<TileData | undefined> {
        const url = this._cleanUrl(srcUrl);
        try {
            const entry = await this._dataCache.get(url);
            if (entry) {
                return entry;
            }
        } catch (_error) {
            return undefined;
        }

        // if we have no tile and we are offline we will search up the tile
        // tree until we find a parent that has data. We then clip this and
        // use that as the image data so we always have something showing.
        // const offline = true;

        // if(offline) {

        // }

        return undefined;
    }
}
